"use client"

import { Suspense, useEffect, useRef, useState } from 'react'
import Head from 'next/head'
import { useSearchParams } from 'next/navigation'
import { Breadcrumb } from "@/components/breadcrumb"
import { LanguageSwitcher } from '@/components/language-switcher'
import { AuthIntegrationChatbotVercelSDK } from '@/components/auth-integration-chatbot-vercel-sdk'
import { track } from '@/lib/analytics'
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { useLanguage } from '@/lib/language-context'
import { AlertTriangle, CheckCircle, Copy, Download, ExternalLink, Github, Info, KeyRound, Lock, Play, Server, Shield, Terminal } from "lucide-react"
import { getTranslation, translations } from '@/lib/translations'

type EndpointParam = {
  name: string
  required: boolean
  location: 'query' | 'body' | 'header'
  description: string
}

type EndpointDoc = {
  method: 'GET' | 'POST'
  path: string
  summary: string
  parameters: EndpointParam[]
  response: string[]
  example: string
  notes?: string[]
}

type ConsoleLine = {
  text: string
  tone?: 'default' | 'success' | 'warning' | 'muted'
}

type SupportedLanguage = keyof typeof translations

const COLLECTION_DOWNLOAD_PATH = '/downloads/AuthFlow_Consumers.yaml'
const PASSWORD_RULE = '^(?:(?=(.*[A-Z]))|(?:.*[a-z]))(?=(.*[0-9]))(?=(.*[!@#$%^&*-])).{8,32}$'
const SAMPLE_FOOBAR_JWT = 'eyJhbGciOiJSUzUxMiIsInR5cCI6IkpXVCJ9.eyJpZGVudGl0eSI6InVzZXJAZXhhbXBsZS5jb20iLCJvcmdfaWQiOiJvcmctdXVpZCIsInJvbGUiOiJkZXRlY3RpdmUiLCJpYXQiOjE3NzUzNDQ4MDAsImV4cCI6MTc3NTM0NDg0NX0.Rm9vQmFyU2lnbmF0dXJlRXhhbXBsZVN0cmluZ0ZvckF1dGhGbG93'
const SAMPLE_SESSION_JWT = 'eyJhbGciOiJSUzUxMiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6InVzZXJAZXhhbXBsZS5jb20iLCJvcmdfaWQiOiJvcmctdXVpZCIsInJvbGUiOiJkZXRlY3RpdmUiLCJpYXQiOjE3NzUzNDQ4MDAsImV4cCI6MTc3NTM4ODAwMH0.U2Vzc2lvblRva2VuU2lnbmF0dXJlRXhhbXBsZVN0cmluZ0ZvckF1dGhGbG93'
const SAMPLE_TEMP_TOKEN = 'd2e6c30aa5f74af4a81f98e8830a6e49e9bb2f31e1a7406a8d2f2eb3ef3028a1'

const DELETE_USER_COPY: Record<SupportedLanguage, {
  summary: string
  authHeader: string
  emailOwner: string
  apiKey: string
  userEmail: string
  forbidden: string
  notFound: string
  noteOwnerOnly: string
  noteTokenMatch: string
}> = {
  en: {
    summary: 'Delete a non-owner auth user in your organization (Integration Owner only).',
    authHeader: 'Session token for the authenticated Integration Owner.',
    emailOwner: 'Owner email. Must match the token identity.',
    apiKey: 'Organization API key. Must belong to the authenticated owner organization.',
    userEmail: 'Email of the user to delete.',
    forbidden: 'or HTTP 403 when attempting to delete owner or without owner permissions',
    notFound: 'or HTTP 404 when user does not exist',
    noteOwnerOnly: 'This endpoint is owner-only and cannot delete the Integration Owner account.',
    noteTokenMatch: 'The token identity and API key owner must match the request body.',
  },
  ru: {
    summary: 'Удаление auth-пользователя (не владельца) в вашей организации (только для владельца организации).',
    authHeader: 'Токен сессии аутентифицированного владельца организации.',
    emailOwner: 'Email владельца. Должен совпадать с identity токена.',
    apiKey: 'API key организации. Должен принадлежать организации аутентифицированного владельца.',
    userEmail: 'Email пользователя, которого нужно удалить.',
    forbidden: 'или HTTP 403 при попытке удалить владельца или без прав владельца',
    notFound: 'или HTTP 404, если пользователь не существует',
    noteOwnerOnly: 'Этот endpoint доступен только владельцу и не может удалить аккаунт владельца организации.',
    noteTokenMatch: 'Identity токена и владелец API key должны совпадать с body запроса.',
  },
  zh: {
    summary: '删除你组织中的非所有者 auth 用户（仅组织所有者可用）。',
    authHeader: '已认证组织所有者的会话令牌。',
    emailOwner: '所有者邮箱。必须与令牌身份一致。',
    apiKey: '组织 API key。必须属于已认证所有者的组织。',
    userEmail: '要删除的用户邮箱。',
    forbidden: '或在尝试删除所有者/无所有者权限时返回 HTTP 403',
    notFound: '或当用户不存在时返回 HTTP 404',
    noteOwnerOnly: '此端点仅限所有者使用，且不能删除组织所有者账号。',
    noteTokenMatch: '令牌身份与 API key 所有者必须与请求体一致。',
  },
  it: {
    summary: 'Elimina un auth user non-owner nella tua organizzazione (solo owner organizzazione).',
    authHeader: 'Token di sessione dell\'owner organizzazione autenticato.',
    emailOwner: 'Email owner. Deve combaciare con l\'identity del token.',
    apiKey: 'API key organizzazione. Deve appartenere all\'organizzazione dell\'owner autenticato.',
    userEmail: 'Email dell\'utente da eliminare.',
    forbidden: 'oppure HTTP 403 quando si tenta di eliminare l\'owner o senza permessi owner',
    notFound: 'oppure HTTP 404 quando l\'utente non esiste',
    noteOwnerOnly: 'Questo endpoint e solo per owner e non puo eliminare l\'account owner dell\'organizzazione.',
    noteTokenMatch: 'Identity del token e owner dell API key devono combaciare con il body della richiesta.',
  },
  pt: {
    summary: 'Exclui um usuario auth nao-owner da sua organizacao (somente owner da organizacao).',
    authHeader: 'Token de sessao do owner autenticado da organizacao.',
    emailOwner: 'Email do owner. Deve corresponder a identidade do token.',
    apiKey: 'API key da organizacao. Deve pertencer a organizacao do owner autenticado.',
    userEmail: 'Email do usuario a ser excluido.',
    forbidden: 'ou HTTP 403 ao tentar excluir owner ou sem permissoes de owner',
    notFound: 'ou HTTP 404 quando o usuario nao existe',
    noteOwnerOnly: 'Este endpoint e apenas para owner e nao pode excluir a conta owner da organizacao.',
    noteTokenMatch: 'A identidade do token e o owner da API key devem corresponder ao body da requisicao.',
  },
  ko: {
    summary: '조직 내 비소유자 auth 사용자를 삭제합니다(조직 소유자 전용).',
    authHeader: '인증된 조직 소유자의 세션 토큰입니다.',
    emailOwner: '소유자 이메일입니다. 토큰 identity와 일치해야 합니다.',
    apiKey: '조직 API key입니다. 인증된 소유자 조직에 속해야 합니다.',
    userEmail: '삭제할 사용자 이메일입니다.',
    forbidden: '또는 소유자 삭제 시도/소유자 권한 없음인 경우 HTTP 403',
    notFound: '또는 사용자가 존재하지 않는 경우 HTTP 404',
    noteOwnerOnly: '이 엔드포인트는 소유자 전용이며 조직 소유자 계정은 삭제할 수 없습니다.',
    noteTokenMatch: '토큰 identity와 API key 소유자는 요청 본문과 일치해야 합니다.',
  },
  de: {
    summary: 'Loescht einen Nicht-Owner auth-Benutzer in deiner Organisation (nur fuer Organisationsinhaber).',
    authHeader: 'Session-Token des authentifizierten Organisationsinhabers.',
    emailOwner: 'Owner-E-Mail. Muss zur Token-Identity passen.',
    apiKey: 'Organisations-API-Key. Muss zur Organisation des authentifizierten Owners gehoeren.',
    userEmail: 'E-Mail des zu loeschenden Benutzers.',
    forbidden: 'oder HTTP 403 bei Loeschversuch des Owners oder ohne Owner-Berechtigung',
    notFound: 'oder HTTP 404, wenn der Benutzer nicht existiert',
    noteOwnerOnly: 'Dieser Endpunkt ist nur fuer Owner und kann den Organisationsinhaber nicht loeschen.',
    noteTokenMatch: 'Token-Identity und API-Key-Owner muessen mit dem Request-Body uebereinstimmen.',
  },
  pl: {
    summary: 'Usuwa nie-owner auth usera w Twojej organizacji (tylko owner organizacji).',
    authHeader: 'Token sesji uwierzytelnionego ownera organizacji.',
    emailOwner: 'Email ownera. Musi zgadzac sie z tozsamoscia tokenu.',
    apiKey: 'API key organizacji. Musi nalezec do organizacji uwierzytelnionego ownera.',
    userEmail: 'Email uzytkownika do usuniecia.',
    forbidden: 'lub HTTP 403 przy probie usuniecia ownera albo bez uprawnien ownera',
    notFound: 'lub HTTP 404 gdy uzytkownik nie istnieje',
    noteOwnerOnly: 'Ten endpoint jest tylko dla ownera i nie moze usunac konta ownera organizacji.',
    noteTokenMatch: 'Tozsamosc tokenu i owner API key musza zgadzac sie z body zadania.',
  },
  fr: {
    summary: 'Supprime un utilisateur auth non-owner de votre organisation (owner uniquement).',
    authHeader: 'Token de session du proprietaire d\'organisation authentifie.',
    emailOwner: 'Email owner. Doit correspondre a l\'identite du token.',
    apiKey: 'API key organisation. Doit appartenir a l\'organisation du owner authentifie.',
    userEmail: 'Email de l\'utilisateur a supprimer.',
    forbidden: 'ou HTTP 403 en cas de tentative de suppression du owner ou sans permissions owner',
    notFound: 'ou HTTP 404 si l\'utilisateur n\'existe pas',
    noteOwnerOnly: 'Cet endpoint est reserve au owner et ne peut pas supprimer le compte owner de l\'organisation.',
    noteTokenMatch: 'L\'identite du token et le owner API key doivent correspondre au body de la requete.',
  },
  yue: {
    summary: '刪除你機構內嘅非 owner auth 用戶（只限機構 owner）。',
    authHeader: '已驗證機構 owner 嘅 session token。',
    emailOwner: 'owner 電郵，必須同 token identity 一致。',
    apiKey: '機構 API key，必須屬於已驗證 owner 嘅機構。',
    userEmail: '要刪除嘅用戶電郵。',
    forbidden: '或者當嘗試刪除 owner／冇 owner 權限時回傳 HTTP 403',
    notFound: '或者當用戶不存在時回傳 HTTP 404',
    noteOwnerOnly: '呢個 endpoint 只限 owner，而且唔可以刪除機構 owner 帳戶。',
    noteTokenMatch: 'token identity 同 API key owner 必須同 request body 一致。',
  },
}

const getSessionEndpoints = (language: SupportedLanguage): EndpointDoc[] => [
  {
    method: 'GET',
    path: '/auth/fooBar',
    summary: getTranslation(language, 'getStarted.endpointContent.session.fooBar.summary'),
    parameters: [
      { name: 'identity', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.fooBar.params.identity') },
      { name: 'api_key', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.fooBar.params.apiKey') },
      { name: 'role', required: false, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.fooBar.params.role') },
    ],
    response: [
      '{',
      `  "token": "${SAMPLE_FOOBAR_JWT}"`,
      '}',
    ],
    example: `curl --get "$AUTHFLOW_BASE_URL/auth/fooBar" \\
  --data-urlencode "identity=user@example.com" \\
  --data-urlencode "api_key=$AUTHFLOW_API_KEY" \\
  --data-urlencode "role=detective"`,
    notes: [
      getTranslation(language, 'getStarted.endpointContent.session.fooBar.notes.expires'),
      getTranslation(language, 'getStarted.endpointContent.session.fooBar.notes.freshToken'),
    ],
  },
  {
    method: 'POST',
    path: '/auth/signUp',
    summary: getTranslation(language, 'getStarted.endpointContent.session.signUp.summary'),
    parameters: [
      { name: 'foobar', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.signUp.params.foobar') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.signUp.params.email') },
      { name: 'password', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.signUp.params.password').replace('{passwordRule}', PASSWORD_RULE) },
      { name: 'fullname', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.signUp.params.fullname') },
      { name: 'role', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.signUp.params.role') },
      { name: 'api_key', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.signUp.params.apiKey') },
      { name: 'disclaimed', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.signUp.params.disclaimed') },
    ],
    response: [
      '{',
      `  "token": "${SAMPLE_SESSION_JWT}"`,
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/signUp" \\
  -H "Content-Type: application/json" \\
  -d '{"foobar":"'$FOOBAR_TOKEN'","api_key":"'$AUTHFLOW_API_KEY'","role":"customer","email":"user@example.com","password":"TestPass123!","fullname":"John E. Smith the 3rd","disclaimed":"true"}'`,
    notes: [
      getTranslation(language, 'getStarted.endpointContent.session.signUp.notes.successToken'),
      getTranslation(language, 'getStarted.endpointContent.session.signUp.notes.duplicateRole'),
    ],
  },
  {
    method: 'POST',
    path: '/auth/login',
    summary: getTranslation(language, 'getStarted.endpointContent.session.login.summary'),
    parameters: [
      { name: 'foobar', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.foobar') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.email') },
      { name: 'password', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.password') },
      { name: 'api_key', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.role') },
    ],
    response: [
      '{',
      `  "token": "${SAMPLE_SESSION_JWT}"`,
      '}',
      'or',
      '{',
      '  "requires2FA": true,',
      `  "tempToken": "${SAMPLE_TEMP_TOKEN}",`,
      '  "email": "user@example.com",',
      '  "role": "detective"',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{"foobar":"'$FOOBAR_TOKEN'","email":"user@example.com","password":"'$USER_PASSWORD'","api_key":"'$AUTHFLOW_API_KEY'","role":"detective"}'`,
    notes: [
      getTranslation(language, 'getStarted.endpointContent.session.login.notes.tempTokenInsteadOfToken'),
      getTranslation(language, 'getStarted.endpointContent.session.login.notes.exchangeThrough2fa'),
    ],
  },
  {
    method: 'GET',
    path: '/auth/validate',
    summary: getTranslation(language, 'getStarted.endpointContent.session.validate.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.validate.params.sessionToken') },
      { name: 'email', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.validate.params.email') },
    ],
    response: [
      'HTTP 200 when valid',
      'HTTP 401 when invalid or expired',
    ],
    example: `curl --get "$AUTHFLOW_BASE_URL/auth/validate" \\
  --data-urlencode "sessionToken=$SESSION_TOKEN" \\
  --data-urlencode "email=user@example.com"`,
  },
  {
    method: 'GET',
    path: '/auth/me',
    summary: getTranslation(language, 'getStarted.endpointContent.session.me.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.me.params.sessionToken') },
    ],
    response: [
      '{',
      '  "email": "user@example.com",',
      '  "fullname": "John Smith",',
      '  "role": "detective"',
      '}',
    ],
    example: `curl --get "$AUTHFLOW_BASE_URL/auth/me" \\
  --data-urlencode "sessionToken=$SESSION_TOKEN"`,
  },
  {
    method: 'GET',
    path: '/auth/logout',
    summary: getTranslation(language, 'getStarted.endpointContent.session.logout.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.logout.params.sessionToken') },
      { name: 'email', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.session.logout.params.email') },
    ],
    response: [
      '{',
      '  "success": true',
      '}',
    ],
    example: `curl --get "$AUTHFLOW_BASE_URL/auth/logout" \\
  --data-urlencode "sessionToken=$SESSION_TOKEN" \\
  --data-urlencode "email=user@example.com"`,
  },
  {
    method: 'POST',
    path: '/auth/deleteUser',
    summary: DELETE_USER_COPY[language].summary,
    parameters: [
      { name: 'Authorization: Bearer <sessionToken>', required: true, location: 'header', description: DELETE_USER_COPY[language].authHeader },
      { name: 'email_owner', required: true, location: 'body', description: DELETE_USER_COPY[language].emailOwner },
      { name: 'api_key', required: true, location: 'body', description: DELETE_USER_COPY[language].apiKey },
      { name: 'user_email', required: true, location: 'body', description: DELETE_USER_COPY[language].userEmail },
    ],
    response: [
      '{',
      '  "success": true,',
      '  "message": "User deleted successfully"',
      '}',
      DELETE_USER_COPY[language].forbidden,
      DELETE_USER_COPY[language].notFound,
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/deleteUser" \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $SESSION_TOKEN" \\
  -d '{"email_owner":"owner@example.com","api_key":"'$AUTHFLOW_API_KEY'","user_email":"member@example.com"}'`,
    notes: [
      DELETE_USER_COPY[language].noteOwnerOnly,
      DELETE_USER_COPY[language].noteTokenMatch,
    ],
  }
]

const getPasswordManagementEndpoints = (language: SupportedLanguage): EndpointDoc[] => [
  {
    method: 'POST',
    path: '/auth/forgot-password',
    summary: getTranslation(language, 'getStarted.endpointContent.passwordManagement.forgotPassword.summary'),
    parameters: [
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.forgotPassword.params.email') },
      { name: 'api_key', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.forgotPassword.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.forgotPassword.params.role') },
    ],
    response: [
      '{',
      '  "success": true,',
      '  "message": "If an account exists for this email, a reset link has been sent."',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/forgot-password" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective"}'`,
  },
  {
    method: 'POST',
    path: '/auth/reset-password',
    summary: getTranslation(language, 'getStarted.endpointContent.passwordManagement.resetPassword.summary'),
    parameters: [
      { name: 'token', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.resetPassword.params.token') },
      { name: 'newPassword', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.resetPassword.params.newPassword').replace('{passwordRule}', PASSWORD_RULE) },
    ],
    response: [
      '{',
      '  "success": true',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/reset-password" \\
  -H "Content-Type: application/json" \\
  -d '{"token":"'$AUTH_RESET_TOKEN'","newPassword":"NewPass123!"}'`,
  },
  {
    method: 'POST',
    path: '/auth/changePassword',
    summary: getTranslation(language, 'getStarted.endpointContent.passwordManagement.changePassword.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.changePassword.params.sessionToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.changePassword.params.email') },
      { name: 'oldPassword', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.changePassword.params.oldPassword') },
      { name: 'newPassword', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.passwordManagement.changePassword.params.newPassword').replace('{passwordRule}', PASSWORD_RULE) },
    ],
    response: [
      'HTTP 200 on success',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/changePassword" \\
  -H "Content-Type: application/json" \\
  -d '{"sessionToken":"'$SESSION_TOKEN'","email":"user@example.com","oldPassword":"'$OLD_PASSWORD'","newPassword":"NewPass123!"}'`,
  },
]

const getEmailVerificationEndpoints = (language: SupportedLanguage): EndpointDoc[] => [
  {
    method: 'GET',
    path: '/auth/verify-email',
    summary: getTranslation(language, 'getStarted.endpointContent.emailVerification.verifyEmail.summary'),
    parameters: [
      { name: 'token', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.emailVerification.verifyEmail.params.token') },
    ],
    response: [
      'HTML verification page',
      'HTTP 200 when verified',
      'HTTP 401 for invalid or expired token',
    ],
    example: `curl --get "$AUTHFLOW_BASE_URL/auth/verify-email" \\
  --data-urlencode "token=$VERIFY_EMAIL_TOKEN"`,
    notes: [
      getTranslation(language, 'getStarted.endpointContent.emailVerification.verifyEmail.notes.openedFromEmail'),
      getTranslation(language, 'getStarted.endpointContent.emailVerification.verifyEmail.notes.frontendHostedProxy'),
    ],
  },
  {
    method: 'GET',
    path: '/auth/resend-verify-email',
    summary: getTranslation(language, 'getStarted.endpointContent.emailVerification.resendVerifyEmail.summary'),
    parameters: [
      { name: 'foobar', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.emailVerification.resendVerifyEmail.params.foobar') },
      { name: 'email', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.emailVerification.resendVerifyEmail.params.email') },
      { name: 'api_key', required: true, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.emailVerification.resendVerifyEmail.params.apiKey') },
      { name: 'role', required: false, location: 'query', description: getTranslation(language, 'getStarted.endpointContent.emailVerification.resendVerifyEmail.params.role') },
    ],
    response: [
      '{',
      '  "statusCode": 200,',
      '  "message": "Verification email sent."',
      '}',
    ],
    example: `curl --get "$AUTHFLOW_BASE_URL/auth/resend-verify-email" \\
  --data-urlencode "foobar=$FOOBAR_TOKEN" \\
  --data-urlencode "email=user@example.com" \\
  --data-urlencode "api_key=$AUTHFLOW_API_KEY" \\
  --data-urlencode "role=detective"`,
  },
]

const getTwoFactorEndpoints = (language: SupportedLanguage): EndpointDoc[] => [
  {
    method: 'POST',
    path: '/auth/2fa/enable',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.enable.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.enable.params.sessionToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.enable.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.enable.params.role') },
    ],
    response: [
      '{',
      '  "secret": "BASE32SECRET",',
      '  "qrCode": "data:image/png;base64,...",',
      '  "otpauth_url": "otpauth://totp/..."',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/enable" \\
  -H "Content-Type: application/json" \\
  -d '{"sessionToken":"'$SESSION_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective"}'`,
  },
  {
    method: 'POST',
    path: '/auth/2fa/verify-setup',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifySetup.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifySetup.params.sessionToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifySetup.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifySetup.params.role') },
      { name: 'totpCode', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifySetup.params.totpCode') },
      { name: 'secret', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifySetup.params.secret') },
    ],
    response: [
      '{',
      '  "success": true,',
      '  "recoveryCodes": ["AB12CD34", "..."]',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/verify-setup" \\
  -H "Content-Type: application/json" \\
  -d '{"sessionToken":"'$SESSION_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective","totpCode":"123456","secret":"'$TOTP_SECRET'"}'`,
  },
  {
    method: 'POST',
    path: '/auth/2fa/disable',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.disable.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.disable.params.sessionToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.disable.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.disable.params.role') },
      { name: 'password', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.disable.params.password') },
    ],
    response: [
      '{',
      '  "success": true',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/disable" \\
  -H "Content-Type: application/json" \\
  -d '{"sessionToken":"'$SESSION_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective","password":"'$USER_PASSWORD'"}'`,
  },
  {
    method: 'POST',
    path: '/auth/2fa/verify-2fa',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.verify2fa.summary'),
    parameters: [
      { name: 'tempToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verify2fa.params.tempToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verify2fa.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verify2fa.params.role') },
      { name: 'totpCode', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verify2fa.params.totpCode') },
    ],
    response: [
      '{',
      `  "token": "${SAMPLE_SESSION_JWT}"`,
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/verify-2fa" \\
  -H "Content-Type: application/json" \\
  -d '{"tempToken":"'$TEMP_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective","totpCode":"123456"}'`,
  },
  {
    method: 'POST',
    path: '/auth/2fa/backup/email',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.backupEmail.summary'),
    parameters: [
      { name: 'tempToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.backupEmail.params.tempToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.backupEmail.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.backupEmail.params.role') },
    ],
    response: [
      '{',
      '  "success": true,',
      '  "message": "OTP sent to your email"',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/backup/email" \\
  -H "Content-Type: application/json" \\
  -d '{"tempToken":"'$TEMP_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective"}'`,
  },
  {
    method: 'POST',
    path: '/auth/2fa/verify-email-otp',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyEmailOtp.summary'),
    parameters: [
      { name: 'tempToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verify2fa.params.tempToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyEmailOtp.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyEmailOtp.params.role') },
      { name: 'emailOTP', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyEmailOtp.params.emailOtp') },
    ],
    response: [
      '{',
      `  "token": "${SAMPLE_SESSION_JWT}"`,
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/verify-email-otp" \\
  -H "Content-Type: application/json" \\
  -d '{"tempToken":"'$TEMP_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective","emailOTP":"123456"}'`,
  },
  {
    method: 'POST',
    path: '/auth/2fa/recovery-codes',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.recoveryCodes.summary'),
    parameters: [
      { name: 'sessionToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.recoveryCodes.params.sessionToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.recoveryCodes.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.recoveryCodes.params.role') },
      { name: 'password', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.recoveryCodes.params.password') },
    ],
    response: [
      '{',
      '  "recoveryCodes": ["AB12CD34", "..."]',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/recovery-codes" \\
  -H "Content-Type: application/json" \\
  -d '{"sessionToken":"'$SESSION_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective","password":"'$USER_PASSWORD'"}'`,
  },
  {
    method: 'POST',
    path: '/auth/2fa/verify-recovery',
    summary: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyRecovery.summary'),
    parameters: [
      { name: 'tempToken', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verify2fa.params.tempToken') },
      { name: 'email', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyRecovery.params.email') },
      { name: 'api_key', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.session.login.params.apiKey') },
      { name: 'role', required: false, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyRecovery.params.role') },
      { name: 'recoveryCode', required: true, location: 'body', description: getTranslation(language, 'getStarted.endpointContent.twoFactor.verifyRecovery.params.recoveryCode') },
    ],
    response: [
      '{',
      `  "token": "${SAMPLE_SESSION_JWT}",`,
      '  "remainingCodes": 9',
      '}',
    ],
    example: `curl -X POST "$AUTHFLOW_BASE_URL/auth/2fa/verify-recovery" \\
  -H "Content-Type: application/json" \\
  -d '{"tempToken":"'$TEMP_TOKEN'","email":"user@example.com","api_key":"'$AUTHFLOW_API_KEY'","role":"detective","recoveryCode":"AB12CD34"}'`,
  },
]

const getEnvironmentVariables = (language: SupportedLanguage) => [
  { name: 'AUTHFLOW_BASE_URL', value: 'https://api.authflow.net', note: getTranslation(language, 'getStarted.endpointContent.environment.baseUrl') },
  { name: 'AUTHFLOW_API_KEY', value: 'af_<32 lowercase hex chars>', note: getTranslation(language, 'getStarted.endpointContent.environment.apiKey') },
  { name: 'AUTHFLOW_DEFAULT_ROLE', value: 'detective', note: getTranslation(language, 'getStarted.endpointContent.environment.defaultRole') },
]

function EndpointCard({ endpoint, showConsoleRunner = false }: { endpoint: EndpointDoc; showConsoleRunner?: boolean }) {
  const { language } = useLanguage()
  const [showConsole, setShowConsole] = useState(false)
  const [isRunning, setIsRunning] = useState(false)
  const [consoleOutput, setConsoleOutput] = useState<ConsoleLine[]>([])
  const [showPrompt, setShowPrompt] = useState(false)
  const [cursorVisible, setCursorVisible] = useState(true)
  const [copiedCurl, setCopiedCurl] = useState(false)
  const [failureMode, setFailureMode] = useState(false)

  const localizeConsole = (key: string, replacements?: Record<string, string | number>) => {
    let value = getTranslation(language, `getStarted.console.${key}`)
    if (!replacements) return value

    for (const [replacementKey, replacementValue] of Object.entries(replacements)) {
      value = value.replace(`{${replacementKey}}`, String(replacementValue))
    }

    return value
  }

  useEffect(() => {
    if (!showConsole) return

    const intervalId = setInterval(() => {
      setCursorVisible((value) => !value)
    }, 530)

    return () => clearInterval(intervalId)
  }, [showConsole])

  const copyCurlExample = () => {
    navigator.clipboard.writeText(endpoint.example)
      .then(() => {
        track('copy_curl_example', {
          endpoint: endpoint.path,
          method: endpoint.method,
        })
        setCopiedCurl(true)
        setTimeout(() => setCopiedCurl(false), 1400)
      })
      .catch((error) => {
        //console.error('Failed to copy curl example:', error)
      })
  }

  const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

  const appendTypedLine = async (line: string, speed = 8, tone: ConsoleLine['tone'] = 'default') => {
    setConsoleOutput((prev) => [...prev, { text: '', tone }])

    for (const char of line) {
      await pause(speed)
      setConsoleOutput((prev) => {
        const next = [...prev]
        const index = next.length - 1
        next[index] = {
          ...next[index],
          text: `${next[index].text}${char}`,
        }
        return next
      })
    }
  }

  const appendBlankLine = () => {
    setConsoleOutput((prev) => [...prev, { text: '' }])
  }

  const getLatencyProfile = () => {
    const baseByEndpoint: Record<string, number> = {
      '/auth/fooBar': 58,
      '/auth/signUp': 132,
      '/auth/verify-email': 118,
      '/auth/resend-verify-email': 104,
      '/auth/login': 126,
      '/auth/forgot-password': 112,
      '/auth/reset-password': 121,
      '/auth/changePassword': 138,
      '/auth/validate': 67,
      '/auth/me': 76,
      '/auth/logout': 71,
    }

    const base = baseByEndpoint[endpoint.path] ?? 92
    const jitter = Math.floor(Math.random() * 36)
    const duration = base + jitter

    return {
      duration,
      waitMs: Math.max(220, Math.min(920, duration * 4)),
    }
  }

  const toneForStatus = (statusLine: string): ConsoleLine['tone'] => {
    if (/HTTP\/1\.1\s2\d\d/.test(statusLine)) return 'success'
    if (/HTTP\/1\.1\s4\d\d|HTTP\/1\.1\s5\d\d/.test(statusLine)) return 'warning'
    return 'default'
  }

  const getStatusVariant = () => {
    if (endpoint.path === '/auth/signUp') {
      return {
        status: localizeConsole('status.signup.success'),
        extras: [localizeConsole('status.signup.alt409')],
        contentType: 'application/json; charset=utf-8',
        exitCode: 0,
      }
    }

    if (endpoint.path === '/auth/login') {
      return {
        status: localizeConsole('status.login.success'),
        extras: [localizeConsole('status.login.alt2fa')],
        contentType: 'application/json; charset=utf-8',
        exitCode: 0,
      }
    }

    if (endpoint.path === '/auth/verify-email') {
      return {
        status: localizeConsole('status.verifyEmail.success'),
        extras: [localizeConsole('status.verifyEmail.alt401')],
        contentType: 'text/html; charset=utf-8',
        exitCode: 0,
      }
    }

    if (endpoint.path === '/auth/validate') {
      return {
        status: localizeConsole('status.validate.success'),
        extras: [localizeConsole('status.validate.alt401')],
        contentType: 'application/json; charset=utf-8',
        exitCode: 0,
      }
    }

    if (endpoint.path === '/auth/forgot-password') {
      return {
        status: localizeConsole('status.forgotPassword.success'),
        extras: [],
        contentType: 'application/json; charset=utf-8',
        exitCode: 0,
      }
    }

    if (endpoint.path === '/auth/changePassword') {
      return {
        status: localizeConsole('status.changePassword.success'),
        extras: [localizeConsole('status.changePassword.noJsonBody')],
        contentType: 'application/json; charset=utf-8',
        exitCode: 0,
      }
    }

    return {
      status: localizeConsole('status.defaultSuccess'),
      extras: [],
      contentType: 'application/json; charset=utf-8',
      exitCode: 0,
    }
  }

  const getFailureVariant = () => {
    if (endpoint.path === '/auth/signUp') {
      return {
        status: localizeConsole('failure.signup.status'),
        response: [
          '{',
          '  "statusCode": 409,',
          '  "message": "User with this email and role already exists."',
          '}',
        ],
        extras: [localizeConsole('failure.signup.extra')],
        contentType: 'application/json; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/login') {
      return {
        status: localizeConsole('failure.login.status'),
        response: [
          '<html>',
          '  <body>This verification link is not valid. Request a fresh verification email from your organization and try again.</body>',
          '</html>',
        ],
        extras: [localizeConsole('failure.login.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/verify-email') {
      return {
        status: localizeConsole('failure.verifyEmail.status'),
        response: [
          '<html>',
          '  <body>This verification link is not valid. Request a fresh verification email.</body>',
          '</html>',
        ],
        extras: [localizeConsole('failure.verifyEmail.extra')],
        contentType: 'text/html; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/validate') {
      return {
        status: localizeConsole('failure.validate.status'),
        response: [],
        extras: [localizeConsole('failure.validate.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/me') {
      return {
        status: localizeConsole('failure.me.status'),
        response: [],
        extras: [localizeConsole('failure.me.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/logout') {
      return {
        status: localizeConsole('failure.logout.status'),
        response: [],
        extras: [localizeConsole('failure.logout.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/changePassword') {
      return {
        status: localizeConsole('failure.changePassword.status'),
        response: [],
        extras: [localizeConsole('failure.changePassword.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/resend-verify-email') {
      return {
        status: localizeConsole('failure.resendVerifyEmail.status'),
        response: [],
        extras: [localizeConsole('failure.resendVerifyEmail.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/fooBar') {
      return {
        status: localizeConsole('failure.fooBar.status'),
        response: ['Bad Request'],
        extras: [localizeConsole('failure.fooBar.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/forgot-password') {
      return {
        status: localizeConsole('failure.forgotPassword.status'),
        response: [],
        extras: [localizeConsole('failure.forgotPassword.extra')],
        contentType: 'text/plain; charset=utf-8',
        exitCode: 22,
      }
    }

    if (endpoint.path === '/auth/reset-password') {
      return {
        status: localizeConsole('failure.resetPassword.status'),
        response: [
          '{',
          '  "message": "Reset token is invalid or expired."',
          '}',
        ],
        extras: [localizeConsole('failure.resetPassword.extra')],
        contentType: 'application/json; charset=utf-8',
        exitCode: 22,
      }
    }

    return {
      status: localizeConsole('failure.default.status'),
      response: ['Bad Request'],
      extras: [localizeConsole('failure.default.extra')],
      contentType: 'text/plain; charset=utf-8',
      exitCode: 22,
    }
  }

  const runConsoleDemo = async () => {
    if (isRunning) return

    track('console_demo_run', {
      endpoint: endpoint.path,
      method: endpoint.method,
      failure_mode: failureMode,
    })

    const promptLine = `${localizeConsole('promptPrefix')} ${endpoint.example.replace(/\\\n/g, ' ').replace(/\s+/g, ' ').trim()}`
    const statusVariant = getStatusVariant()
    const failureVariant = getFailureVariant()
    const activeStatus = failureMode ? failureVariant : statusVariant
    const activeResponse = failureMode ? failureVariant.response : endpoint.response
    const latency = getLatencyProfile()

    setShowConsole(true)
    setIsRunning(true)
    setShowPrompt(false)
    setConsoleOutput([])

    try {
      await appendTypedLine(localizeConsole('launching'), 5, 'muted')
      await appendTypedLine(localizeConsole('kernel'), 4, 'muted')
      await appendTypedLine(localizeConsole('curlVersion'), 4, 'muted')
      await appendTypedLine(localizeConsole('connected'), 5, 'default')
      appendBlankLine()
      await appendTypedLine(promptLine, 1)
      appendBlankLine()
      await appendTypedLine(localizeConsole('sending', { method: endpoint.method, path: endpoint.path }), 5, 'muted')
      await appendTypedLine(localizeConsole('waiting'), 5, 'muted')
      await pause(latency.waitMs)
      appendBlankLine()
      await appendTypedLine(`< ${activeStatus.status}`, 4, toneForStatus(activeStatus.status))
      await appendTypedLine(localizeConsole('serverHeader'), 2, 'muted')
      await appendTypedLine(`< content-type: ${activeStatus.contentType}`, 2, 'muted')
      await appendTypedLine(`< server-timing: total;dur=${latency.duration}`, 2, 'muted')

      for (const line of activeResponse) {
        await appendTypedLine(`< ${line}`, 2, 'default')
      }

      for (const extraLine of activeStatus.extras) {
        const tone = /HTTP\/1\.1\s4\d\d|HTTP\/1\.1\s5\d\d/.test(extraLine) ? 'warning' : 'muted'
        await appendTypedLine(`< ${extraLine}`, 2, tone)
      }

      const exitTone: ConsoleLine['tone'] = activeStatus.exitCode === 0 ? 'success' : 'warning'
      setConsoleOutput((prev) => [...prev, { text: '' }, { text: localizeConsole('processExited', { code: activeStatus.exitCode }), tone: exitTone }])
      setShowPrompt(true)
    } finally {
      setIsRunning(false)
    }
  }

  const getLineClassName = (tone: ConsoleLine['tone']) => {
    if (tone === 'success') return 'text-emerald-300'
    if (tone === 'warning') return 'text-amber-300'
    if (tone === 'muted') return 'text-zinc-400'
    return 'text-emerald-300'
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={endpoint.method === 'GET' ? 'secondary' : 'default'}>{endpoint.method}</Badge>
          <CardTitle className="font-mono text-sm break-all">{endpoint.path}</CardTitle>
        </div>
        <p className="text-muted-foreground">{endpoint.summary}</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <h4 className="font-semibold mb-2">{getTranslation(language, 'getStarted.parameters')}</h4>
          <div className="space-y-2 text-sm text-muted-foreground">
            {endpoint.parameters.map((param) => (
              <div key={`${endpoint.path}-${param.name}`} className="rounded border border-border bg-muted/40 p-3">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <code>{param.name}</code>
                  <Badge variant="outline">{param.location}</Badge>
                  <Badge variant={param.required ? 'default' : 'secondary'}>{param.required ? getTranslation(language, 'getStarted.required') : getTranslation(language, 'getStarted.optional')}</Badge>
                </div>
                <p>{param.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-2">{getTranslation(language, 'getStarted.response')}</h4>
          <pre className="bg-muted rounded p-4 text-sm overflow-x-auto whitespace-pre-wrap break-all">{endpoint.response.join('\n')}</pre>
        </div>

        <div>
          <div className="mb-2 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
            <h4 className="font-semibold">{getTranslation(language, 'getStarted.example')}</h4>
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {showConsoleRunner && (
                <Button
                  size="sm"
                  className={`h-auto whitespace-normal text-left ${failureMode ? 'bg-red-600 text-white hover:bg-red-500 border-red-600' : 'bg-red-700 text-white hover:bg-red-600 border-red-700'}`}
                  onClick={() => {
                    const newMode = !failureMode
                    if (newMode) {
                      track('failure_mode_toggled', {
                        endpoint: endpoint.path,
                        mode: 'on',
                      })
                    }
                    setFailureMode(newMode)
                  }}
                  disabled={isRunning}
                >
                  {failureMode ? getTranslation(language, 'getStarted.failureScenarioOn') : getTranslation(language, 'getStarted.runFailureScenario')}
                </Button>
              )}
              {showConsoleRunner && (
                <Button
                  size="sm"
                  className={`h-auto whitespace-normal text-left ${failureMode ? 'bg-amber-500 text-zinc-900 hover:bg-amber-400' : 'bg-emerald-600 text-white hover:bg-emerald-500'}`}
                  onClick={runConsoleDemo}
                  disabled={isRunning}
                >
                  <Play className="h-4 w-4 mr-2" />
                  {isRunning ? getTranslation(language, 'getStarted.running') : getTranslation(language, 'getStarted.runCurlConsole')}
                </Button>
              )}
              <Button variant="ghost" size="sm" className="h-auto whitespace-normal text-left" onClick={copyCurlExample}>
                <Copy className="h-4 w-4 mr-2" />
                {copiedCurl ? getTranslation(language, 'getStarted.copied') : getTranslation(language, 'getStarted.copyCurl')}
              </Button>
            </div>
          </div>
          <pre className="bg-muted rounded p-4 text-sm overflow-x-auto whitespace-pre-wrap break-all">{endpoint.example}</pre>
        </div>

        {showConsoleRunner && showConsole && (
          <div>
            <h4 className="font-semibold mb-2">{getTranslation(language, 'getStarted.consoleDemoTitle')}</h4>
            <div className="rounded-md border border-emerald-700/50 overflow-hidden">
              <div className="flex items-center gap-2 bg-zinc-900 text-zinc-100 px-3 py-2 text-xs border-b border-zinc-700">
                <Terminal className="h-4 w-4 text-emerald-400" />
                <span>{getTranslation(language, 'getStarted.consoleShellLabel')}</span>
              </div>
              <div className="bg-black text-emerald-300 p-4 text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap break-all font-mono min-h-[180px]">
                <pre>
                  {consoleOutput.map((line, idx) => (
                    <span key={`${endpoint.path}-line-${idx}`} className={`block ${getLineClassName(line.tone)}`}>
                      {line.text}
                    </span>
                  ))}
                </pre>
                {showPrompt && !isRunning && (
                  <div className="mt-2">
                    <span>{localizeConsole('promptPrefix')} </span>
                    <span className={`inline-block w-2 h-4 align-middle ${cursorVisible ? 'bg-emerald-300' : 'bg-transparent'}`} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {endpoint.notes && endpoint.notes.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2">{getTranslation(language, 'getStarted.notesTitle')}</h4>
            <div className="space-y-2 text-sm text-muted-foreground">
              {endpoint.notes.map((note) => (
                <div key={`${endpoint.path}-${note}`} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  <p>{note}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function GetStartedPageContent() {
  const { t, language } = useLanguage()
  const searchParams = useSearchParams()
  const [copiedBackendExample, setCopiedBackendExample] = useState(false)
  const [liveVisitorId, setLiveVisitorId] = useState('')
  const hasTrackedVisitRef = useRef(false)
  const sessionEndpoints = getSessionEndpoints(language)
  const passwordManagementEndpoints = getPasswordManagementEndpoints(language)
  const emailVerificationEndpoints = getEmailVerificationEndpoints(language)
  const twoFactorEndpoints = getTwoFactorEndpoints(language)
  const environmentVariables = getEnvironmentVariables(language)
  const operatorVisitorId = searchParams.get('operatorVisitorId') || ''

  useEffect(() => {
    if (hasTrackedVisitRef.current) {
      return
    }
    hasTrackedVisitRef.current = true

    if (typeof window === 'undefined') {
      return
    }

    const storageKey = 'authflow_whatsapp_visitor_id'
    let visitorId = window.localStorage.getItem(storageKey)

    if (!visitorId || !visitorId.trim()) {
      visitorId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? `wa_${crypto.randomUUID()}`
          : `wa_${Date.now()}_${Math.random().toString(16).slice(2)}`
      window.localStorage.setItem(storageKey, visitorId)
    }

    setLiveVisitorId(visitorId)

    track('get_started_page_visit', {
      source: 'get_started_page',
      visitor_id: visitorId,
      language,
    })

  }, [language])

  const getPageContentArray = (
    key: 'integrationRulesRules' | 'collectionItems' | 'implementStepRules' | 'connectStepRules' | 'securityRequirements'
  ): string[] => {
    const localized = (translations[language]?.getStarted?.pageContent as any)?.[key]
    if (Array.isArray(localized)) {
      return localized
    }

    const fallback = (translations.en.getStarted.pageContent as any)?.[key]
    return Array.isArray(fallback) ? fallback : []
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => {
        track('copy_backend_example', {
          source: 'get_started_page',
        })
        setCopiedBackendExample(true)
        setTimeout(() => setCopiedBackendExample(false), 1400)
      })
      .catch((error) => {
        //console.error('Failed to copy example:', error)
      })
  }

  const backendExample = `// lib/auth.ts using fetch with React statefulness
  
import { UserRole } from './types'
import { updateState } from './store'

// Cookie helpers — use Secure + SameSite=Strict for security.

function setCookie(name: string, value: string, days = 7): void {
  if (typeof document === 'undefined') return
  const expires = new Date(Date.now() + days * 864e5).toUTCString()
  document.cookie = \`\${name}=\${encodeURIComponent(value)}; expires=\${expires}; path=/; SameSite=Strict; Secure\`
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : null
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return
  const isLocalhost = window.location.hostname === 'localhost'
  document.cookie = \`\${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Strict; Secure\`
}

export interface AuthUser {
  email: string
  fullname: string
  role: UserRole
  token: string
}

export interface AuthState {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
}

class AuthFlowClient {
  private baseUrl: string
  private apiKey: string

  constructor() {
    const isLocalhost = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    this.baseUrl = process.env.AUTHFLOW_API_URL
    this.apiKey = process.env.AUTHFLOW_API_KEY
  }

  private async makeRequest(endpoint: string, params: Record<string, string>, method: 'GET' | 'POST' = 'GET'): Promise<{ data: any; status: number }> {
    let response: Response

    if (method === 'POST') {
      response = await fetch(\`\${this.baseUrl}\${endpoint}\`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
      })
    } else {
      const url = new URL(\`\${this.baseUrl}\${endpoint}\`)
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.append(key, value)
      })
      response = await fetch(url.toString())
    }
    
    let data: any
    const contentType = response.headers.get('content-type')
    
    if (contentType && contentType.includes('application/json')) {
      data = await response.json()
    } else {
      // Handle non-JSON responses (like plain text error messages)
      const text = await response.text()
      data = { message: text, status: response.status }
    }

    return { data, status: response.status }
  }

  async getFooBarToken(email: string, role?: string): Promise<string> {
    const { data: response } = await this.makeRequest('/auth/fooBar', { identity: email, api_key: this.apiKey, ...(role && { role }) })
    return response.token
  }

  async signUp(email: string, password: string, fullname: string, role?: string): Promise<AuthUser> {
    const foobar = await this.getFooBarToken(email, role)

    const { data: response, status } = await this.makeRequest('/auth/signUp', {
      foobar,
      api_key: this.apiKey,
      email,
      password,
      fullname,
      disclaimed: 'true',
      ...(role && { role })
    }, 'POST')

    // Handle different error status codes
    if (status === 400) {
      if (response.message?.includes('Password does not meet complexity')) {
        throw new Error('Password must be at least 8 characters long and contain uppercase, lowercase, and numbers')
      } else if (response.message?.includes('Missing required fields')) {
        throw new Error('Please fill in all required fields')
      } else {
        throw new Error('Invalid signup information. Please check your details and try again.')
      }
    } else if (status === 401) {
      if (response.message?.includes('Invalid or expired token')) {
        throw new Error('Signup session expired. Please refresh the page and try again.')
      } else if (response.message?.includes('Invalid api_key')) {
        throw new Error('Service temporarily unavailable. Please try again later.')
      } else if (response.message?.includes('email verification')) {
        throw new Error('Email verification is required. Please check your email for verification instructions.')
      } else {
        throw new Error('Unable to create account. Please try again later.')
      }
    } else if (status === 409) {
      throw new Error('An account with this email and role already exists. Please sign in instead or choose a different role.')
    } else if (status === 500) {
      throw new Error('Server error. Please try again later.')
    } else if (status !== 200) {
      throw new Error('Unable to create account. Please try again.')
    }

    if (role === 'admin') {
      const adminUserData = {
        email: response?.email || email,
        fullname: response?.fullname || fullname,
        role: 'admin' as UserRole,
        token: '',
      }
      // Update the app store with the admin user data
      updateState({ user: adminUserData })
      return adminUserData
    }

    // For non-admin roles, login after signup to get the token
    return this.signIn(email, password, role)
  }

  async signIn(email: string, password: string, role?: string): Promise<AuthUser> {
    const foobar = await this.getFooBarToken(email, role)

    const { data: response, status } = await this.makeRequest('/auth/login', {
      foobar,
      email,
      password,
      api_key: this.apiKey,
      ...(role && { role })
    }, 'POST')

    // Handle different error status codes
    if (status === 400) {
      throw new Error('Please provide both email and password')
    } else if (status === 401) {
      if (response.message?.includes('Incorrect password')) {
        throw new Error('Incorrect password. Please check your password and try again.')
      } else if (response.message?.includes('Invalid api_key')) {
        throw new Error('Service temporarily unavailable. Please try again later.')
      } else {
        throw new Error('Invalid login credentials. Please check your email and password.')
      }
    } else if (status === 404) {
      throw new Error('There is no user with that signin info, please use the signup link.')
    } else if (status === 500) {
      throw new Error('Server error. Please try again later.')
    } else if (status !== 200) {
      throw new Error('Unable to sign in. Please try again.')
    }

    if (response.requires2FA) {
      // TODO: 2FA implementation - this is a placeholder to indicate where 2FA logic would go
      throw new Error('2FA required - not implemented yet')
    }

    // Get user details using /auth/me
    const { data: userResponse, status: meStatus } = await this.makeRequest('/auth/me', {
      sessionToken: response.token
    })

    if (meStatus !== 200) {
      if (meStatus === 401) {
        throw new Error('Session expired. Please sign in again.')
      } else {
        throw new Error('Unable to retrieve user information. Please try again.')
      }
    }

    // Ensure we have valid user data
    if (!userResponse || typeof userResponse !== 'object' || !userResponse.email) {
      throw new Error('Invalid user data received. Please try again.')
    }

    const userData = {
      email: userResponse.email,
      fullname: userResponse.fullname,
      role: userResponse.role as UserRole,
      token: response.token
    }

    // Update the app store with the authenticated user data
    updateState({ user: userData })

    return userData
  }

  async signOut(token: string, email: string): Promise<void> {
    await this.makeRequest('/auth/logout', {
      sessionToken: token,
      email
    })
    // Clear the user data from the app store
    updateState({ user: null })
  }

  async validateToken(token: string, email: string): Promise<boolean> {
    try {
      await this.makeRequest('/auth/validate', {
        sessionToken: token,
        email
      })
      return true
    } catch {
      return false
    }
  }

  async changePassword(sessionToken: string, email: string, oldPassword: string, newPassword: string): Promise<void> {
    const { status } = await this.makeRequest('/auth/changePassword', {
      sessionToken,
      email,
      oldPassword,
      newPassword
    }, 'POST')

    // Handle different error status codes
    if (status === 400) {
      if (newPassword.length < 8 || !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
        throw new Error('Password must be at least 8 characters long and contain uppercase, lowercase, and numbers')
      } else {
        throw new Error('Missing required fields. Please provide email, old password, and new password.')
      }
    } else if (status === 401) {
      throw new Error('Invalid session or incorrect old password. Please check your credentials and try again.')
    } else if (status === 404) {
      throw new Error('User not found. Please check your email address.')
    } else if (status === 500) {
      throw new Error('Server error. Please try again later.')
    } else if (status !== 200) {
      throw new Error('Unable to change password. Please try again.')
    }
  }
}

export const authFlowClient = new AuthFlowClient()

// Auth state management
let authState: AuthState = {
  user: null,
  isLoading: false,
  isAuthenticated: false
}

const listeners: ((state: AuthState) => void)[] = []

export function subscribeToAuthState(listener: (state: AuthState) => void) {
  listeners.push(listener)
  return () => {
    const index = listeners.indexOf(listener)
    if (index > -1) {
      listeners.splice(index, 1)
    }
  }
}

function notifyListeners() {
  listeners.forEach(listener => listener(authState))
}

export function getAuthState(): AuthState {
  return { ...authState }
}

export async function signUp(email: string, password: string, fullname: string, role?: string): Promise<AuthUser> {
  authState.isLoading = true
  notifyListeners()

  try {
    const user = await authFlowClient.signUp(email, password, fullname, role)
    authState.user = user.token ? user : null
    authState.isAuthenticated = Boolean(user.token)
    authState.isLoading = false
    notifyListeners()

    if (user.token) {
      setCookie('auth_user', JSON.stringify(user))
    }

    return user
  } catch (error) {
    authState.isLoading = false
    notifyListeners()
    throw error
  }
}

export async function signIn(email: string, password: string, role?: string): Promise<AuthUser> {
  authState.isLoading = true
  notifyListeners()

  try {
    const user = await authFlowClient.signIn(email, password, role)
    authState.user = user
    authState.isAuthenticated = true
    authState.isLoading = false
    notifyListeners()

    // Store in secure cookie
    setCookie('auth_user', JSON.stringify(user))

    return user
  } catch (error) {
    authState.isLoading = false
    notifyListeners()
    throw error
  }
}

export async function signOut(): Promise<void> {
  if (authState.user) {
    try {
      await authFlowClient.signOut(authState.user.token, authState.user.email)
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  authState.user = null
  authState.isAuthenticated = false
  authState.isLoading = false
  notifyListeners()

  // Clear auth cookie
  deleteCookie('auth_user')
}

export async function initializeAuth(): Promise<void> {
  const stored = getCookie('auth_user')
  if (stored) {
    try {
      const user = JSON.parse(stored)
      // Validate token
      const isValid = await authFlowClient.validateToken(user.token, user.email)
      if (isValid) {
        authState.user = user
        authState.isAuthenticated = true
        notifyListeners()
        // Update the app store with the restored user data
        updateState({ user })
      } else {
        deleteCookie('auth_user')
        // Clear the user data from the app store
        updateState({ user: null })
      }
    } catch (error) {
      console.error('Error initializing auth:', error)
      deleteCookie('auth_user')
      // Clear the user data from the app store
      updateState({ user: null })
    }
  }
}`

  return (
    <>
      <Head>
        <title>{t('getStarted.title')}</title>
        <meta name="description" content="Exact AuthFlow™ authentication-user integration guide, endpoint reference, and downloadable Insomnia collection for Integration Owners." />
        <meta name="keywords" content="AuthFlow™, auth API, /auth endpoints, 2FA API, Insomnia collection, backend authentication integration" />
      </Head>

      <div className="min-h-screen bg-background [overflow-wrap:anywhere]">
        <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <a href="/" className="flex shrink-0 items-center space-x-2" aria-label="AuthFlow™ home">
                <Shield className="h-8 w-8 shrink-0 text-accent" />
                <span className="hidden text-xl font-bold text-foreground sm:inline">AuthFlow™</span>
              </a>
              <nav className="hidden xl:flex items-center gap-6">
                <a href="/#features" className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.features')}
                </a>
                <a href="/pricing" className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.pricing')}
                </a>
                <a href="mailto:support@authflow.net" className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors">
                  {t('nav.contact')}
                </a>
              </nav>
              <div className="flex min-w-0 items-center gap-1 sm:gap-4">
                <LanguageSwitcher className="px-2 sm:px-3" />
                <a href="/sign-in" className="hidden sm:block">
                  <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
                    {t('nav.signIn')}
                  </Button>
                </a>
                <a href="/sign-up">
                  <Button className="px-3 text-sm bg-primary text-primary-foreground hover:bg-primary/90 sm:px-4">{t('nav.getStarted')}</Button>
                </a>
              </div>
            </div>
          </div>
        </header>

        <div className="border-b border-border bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Breadcrumb items={[
              { label: t('getStarted.breadcrumb.resources'), href: "/get-started" },
              { label: t('getStarted.breadcrumb.getStarted') }
            ]} />
          </div>
        </div>

        <section className="py-20 lg:py-28">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto text-center">
              <Badge variant="secondary" className="mb-6 bg-accent/10 text-accent border-accent/20">
                {t('getStarted.pageContent.badge')}
              </Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                {t('getStarted.pageContent.title')}
                <span className="text-accent block">{t('getStarted.pageContent.titleAccent')}</span>
              </h1>
                  <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed mb-8">
                  {t('getStarted.pageContent.description')}
                </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={COLLECTION_DOWNLOAD_PATH} download>
                  <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3">
                    <Download className="h-4 w-4 mr-2" />
                    {t('getStarted.pageContent.downloadCollection')}
                  </Button>
                </a>
                <a href="/sign-up">
                  <Button size="lg" variant="outline" className="px-8 py-3">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {t('getStarted.pageContent.createAccount')}
                  </Button>
                </a>
                <div className="flex items-center justify-center gap-2">
                  <a href="https://github.com/AuthFlow-net/authflow-nextjs-starter" target="_blank" rel="noopener noreferrer nofollow">
                    <Button size="lg" variant="outline" className="px-8 py-3">
                      <Github className="h-4 w-4 mr-2" />
                      {t('getStarted.pageContent.cloneStarterOnGitHub')}
                    </Button>
                  </a>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-10 w-10 shrink-0 border-amber-500/70 text-amber-700 hover:bg-amber-50 hover:text-amber-800 dark:text-amber-300 dark:hover:bg-amber-950/40"
                        aria-label={t('getStarted.pageContent.starterInfoLabel')}
                      >
                        <Info className="h-5 w-5" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="center" className="max-h-[var(--radix-popover-content-available-height)] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto border-amber-500/60 p-5 text-left">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div className="space-y-3">
                          <h2 className="font-semibold text-foreground">{t('getStarted.pageContent.starterWarningTitle')}</h2>
                          <p className="text-sm leading-relaxed text-muted-foreground">{t('getStarted.pageContent.starterWarningPrototype')}</p>
                          <p className="text-sm font-medium leading-relaxed text-foreground">{t('getStarted.pageContent.starterWarningSecret')}</p>
                          <p className="text-sm leading-relaxed text-muted-foreground">{t('getStarted.pageContent.starterWarningProduction')}</p>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-16" aria-labelledby="express-proxy-title">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
              <Card className="border-emerald-600/40 bg-card shadow-sm">
                <CardHeader>
                  <CardTitle id="express-proxy-title" className="flex items-center gap-2 text-2xl">
                    <Server className="h-6 w-6 shrink-0 text-emerald-600 dark:text-emerald-400" />
                    <span>
                      {t('getStarted.pageContent.proxyTitlePrefix')}
                      <span className="whitespace-nowrap">AuthFlow<sup>TM</sup></span>
                      {t('getStarted.pageContent.proxyTitleSuffix')}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5 text-muted-foreground">
                  <div className="flex items-start gap-3 rounded-md border border-amber-500/60 bg-amber-50 p-4 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100" role="alert">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" />
                    <div className="space-y-1">
                      <p className="font-semibold">{t('getStarted.pageContent.apiKeyWarningTitle')}</p>
                      <p className="text-sm leading-relaxed">{t('getStarted.pageContent.apiKeyWarningBody')}</p>
                    </div>
                  </div>
                  <p>
                    {t('getStarted.pageContent.proxyDescriptionPrefix')}AuthFlow<sup>TM</sup>{t('getStarted.pageContent.proxyDescriptionSuffix')}
                  </p>
                  <p>{t('getStarted.pageContent.proxyDetailsRoutes')}</p>
                  <p>{t('getStarted.pageContent.proxyDetailsDeploy')}</p>
                  <a
                    href="https://github.com/AuthFlow-net/authflow-expressjs-proxy"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="inline-block"
                  >
                    <Button className="bg-[#2da44e] text-white hover:bg-[#2c974b]">
                      <Github className="h-4 w-4 mr-2" />
                      {t('getStarted.pageContent.proxyButton')}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="pb-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mx-auto grid max-w-6xl grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-3">
              <Card className="min-w-0 bg-card border-border lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5 text-accent" />
                    {t('getStarted.pageContent.integrationRulesTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  {getPageContentArray('integrationRulesRules').map((rule, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <p>{rule}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="min-w-0 bg-card border-border">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <KeyRound className="h-5 w-5 text-accent" />
                    {t('getStarted.pageContent.ownerAssetsTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground">
                  <p>{t('getStarted.pageContent.ownerAssetsText')}</p>
                  <a href={COLLECTION_DOWNLOAD_PATH} download className="block min-w-0">
                    <Button className="h-auto w-full min-w-0 whitespace-normal py-2 text-center">
                      <Download className="h-4 w-4 mr-2" />
                      {t('getStarted.pageContent.ownerAssetsCollectionName')}
                    </Button>
                  </a>
                  <p>{t('getStarted.pageContent.collectionIncludes')}</p>
                  <div className="space-y-2">
                    {getPageContentArray('collectionItems').map((item, index) => (
                      <div key={index}>{item}</div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{t('getStarted.pageContent.checklistTitle')}</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t('getStarted.pageContent.checklistSubtitle')}</p>
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)] gap-6 md:grid-cols-3">
                <Card className="min-w-0 bg-card border-border">
                  <CardHeader>
                    <CardTitle>{t('getStarted.pageContent.configStep')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    {environmentVariables.map((item) => (
                      <div key={item.name} className="rounded border border-border bg-muted/40 p-3">
                        <div className="font-mono text-foreground break-all">{item.name}={item.value}</div>
                        <p className="mt-2">{item.note}</p>
                      </div>
                    ))}
                  </CardContent>
                </Card>

                <Card className="min-w-0 bg-card border-border">
                  <CardHeader>
                    <CardTitle>{t('getStarted.pageContent.implementStep')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>{t('getStarted.pageContent.checklistSubtitle')}</p>
                    <div className="space-y-2">
                      {getPageContentArray('implementStepRules').map((rule, index) => (
                        <div key={index}>{rule}</div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="min-w-0 bg-card border-border">
                  <CardHeader>
                    <CardTitle>{t('getStarted.pageContent.connectStep')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm text-muted-foreground">
                    <p>{t('getStarted.pageContent.connectStepSecurityNote')}</p>
                    <div className="space-y-2">
                      {getPageContentArray('connectStepRules').map((rule, index) => (
                        <div key={index}>{rule}</div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto space-y-8">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{t('getStarted.pageContent.sessionEndpointsTitle')}</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t('getStarted.pageContent.sessionEndpointsSubtitle')}</p>
              </div>
              {sessionEndpoints.map((endpoint) => (
                <EndpointCard key={endpoint.path} endpoint={endpoint} showConsoleRunner />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto space-y-8">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{t('getStarted.pageContent.passwordMgmtTitle')}</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t('getStarted.pageContent.passwordMgmtSubtitle')}</p>
              </div>
              {passwordManagementEndpoints.map((endpoint) => (
                <EndpointCard key={endpoint.path} endpoint={endpoint} showConsoleRunner />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto space-y-8">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{t('getStarted.pageContent.emailVerificationTitle')}</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t('getStarted.pageContent.emailVerificationSubtitle')}</p>
              </div>
              {emailVerificationEndpoints.map((endpoint) => (
                <EndpointCard key={endpoint.path} endpoint={endpoint} showConsoleRunner />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto space-y-8">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{t('getStarted.pageContent.twoFactorTitle')}</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{t('getStarted.pageContent.twoFactorSubtitle')}</p>
              </div>
              {twoFactorEndpoints.map((endpoint) => (
                <EndpointCard key={endpoint.path} endpoint={endpoint} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="mx-auto grid max-w-7xl grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-3">
                <Card className="min-w-0 bg-card border-border lg:col-span-2">
                <CardHeader>
                  <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <CardTitle className="flex items-center gap-2">
                      <Server className="h-5 w-5 text-accent" />
                      {t('getStarted.pageContent.serviceExampleTitle')}
                    </CardTitle>
                    <Button variant="ghost" size="sm" className="h-auto max-w-full whitespace-normal text-left" onClick={() => copyToClipboard(backendExample)}>
                      <Copy className="h-4 w-4 mr-2" />
                      {copiedBackendExample ? getTranslation(language, 'getStarted.copied') : getTranslation(language, 'getStarted.copyBackendExample')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <pre className="bg-muted rounded p-4 text-sm overflow-x-auto whitespace-pre-wrap break-all">{backendExample}</pre>
                </CardContent>
              </Card>

              <Card className="min-w-0 bg-card border-border self-start h-fit">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="h-5 w-5 text-accent" />
                    {t('getStarted.pageContent.securityRequirementsTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 text-sm text-muted-foreground pb-4">
                  {getPageContentArray('securityRequirements').map((req, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                      <p>{req}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <section className="py-20 bg-accent">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto">
              <h2 className="text-3xl lg:text-4xl font-bold text-accent-foreground mb-6">{t('getStarted.pageContent.readyCtaTitle')}</h2>
              <p className="text-xl text-accent-foreground/80 mb-8">{t('getStarted.pageContent.readyCtaDescription')}</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={COLLECTION_DOWNLOAD_PATH} download>
                  <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3">
                    <Download className="h-4 w-4 mr-2" />
                    {t('getStarted.pageContent.downloadCollection')}
                  </Button>
                </a>
                <a href="/sign-up">
                  <Button size="lg" variant="outline" className="border-accent-foreground/20 text-accent-foreground hover:bg-accent-foreground/10 px-8 py-3 bg-transparent">
                    {t('getStarted.pageContent.createAccount')}
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <footer className="py-12 border-t border-border">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Shield className="h-6 w-6 text-accent" />
                  <span className="text-lg font-bold text-foreground">AuthFlow™</span>
                </div>
                <p className="text-muted-foreground">{t('pricing.footer.description')}</p>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-4">{t('pricing.footer.product.title')}</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/#features" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.product.features')}
                    </a>
                  </li>
                  <li>
                    <a href="/pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.product.pricing')}
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-4">{t('pricing.footer.resources.title')}</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/get-started" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.resources.gettingStarted')}
                    </a>
                  </li>
                  <li>
                    <a href="/get-started" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.resources.apiReference')}
                    </a>
                  </li>
                  <li>
                    <a href="mailto:support@authflow.net" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.resources.support')}
                    </a>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-foreground mb-4">{t('pricing.footer.company.title')}</h3>
                <ul className="space-y-2">
                  <li>
                    <a href="/about" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.company.about')}
                    </a>
                  </li>
                  <li>
                    <a href="/privacy-policy" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.company.privacy')}
                    </a>
                  </li>
                  <li>
                    <a href="/terms-of-service" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('pricing.footer.company.terms')}
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="border-t border-border mt-8 pt-8 text-center">
              <p className="text-muted-foreground">{t('pricing.footer.copyright')}</p>
            </div>
          </div>
        </footer>

        <AuthIntegrationChatbotVercelSDK visitorId={liveVisitorId} />
      </div>
    </>
  )
}

export default function GetStartedPage() {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
      <GetStartedPageContent />
    </Suspense>
  )
}
